import { Beer, MapPin, Search, Utensils } from 'lucide-react';
import React from 'react';
import { Category, PopulatedVenue } from '../types';
import { StatusStamp } from './StatusStamp';

interface VenueListProps {
  venues: PopulatedVenue[];
  searchTerm: string;
  setSearchTerm: (s: string) => void;
  onSelectVenue: (id: string) => void;
}

export const VenueList: React.FC<VenueListProps> = ({ venues, searchTerm, setSearchTerm, onSelectVenue }) => {
  return (
    <div className="w-full h-full flex flex-col bg-brand-cream">
      
      {/* Search Bar */}
      {/* Filtering lives in the bottom control bar, so the search row stays full-width. */}
      <div className="p-4 pt-24 pb-4 bg-brand-cream/95 backdrop-blur-sm z-10 sticky top-0 border-b border-brand-cream-dark">
         <div className="relative">
            <Search className="absolute left-3 top-3.5 text-brand-dark-light" size={20} />
            <input
                type="text"
                placeholder="Search places..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-3 pl-10 bg-white border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red text-brand-dark placeholder:text-brand-dark-light font-medium"
            />
         </div>
      </div>

      {/* List */}
      <ul className="flex-1 overflow-y-auto p-4 pb-28 space-y-3">
         {venues.length === 0 ? (
             <div className="flex flex-col items-center justify-center py-20 text-brand-dark-light">
                 <MapPin size={48} className="mb-4 opacity-20" />
                 <p className="font-bold">No places found</p>
                 <p className="text-sm">Try adjusting your filters or search</p>
             </div>
         ) : (
             venues.map((venue, index) => {
                 const isFood = venue.category === Category.FOOD;
                 
                 return (
                     <li 
                        key={venue.venueId}
                        onClick={() => onSelectVenue(venue.venueId)}
                        className="bg-white rounded-2xl p-3 pr-4 shadow-sm border border-brand-cream-dark active:scale-[0.98] transition-transform cursor-pointer flex items-center gap-4 animate-slide-in"
                        style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                     >
                         {/* Leading Category Icon */}
                         <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${isFood ? 'bg-cat-food-bg text-cat-food-fg' : 'bg-cat-drinks-bg text-cat-drinks-fg'}`}>
                             {isFood ? <Utensils size={24} strokeWidth={2.5} /> : <Beer size={24} strokeWidth={2.5} />}
                         </div>

                         <div className="flex-1 min-w-0">
                             <div className="flex justify-between items-start gap-2">
                                 <h3 className="font-display font-extrabold text-lg text-brand-dark leading-tight line-clamp-2 pr-1">{venue.name}</h3>
                                 <StatusStamp status={venue.primaryStatus} size="sm" rotate={false} labelMaxWidth="70px" />
                             </div>
                             
                             <div className="flex items-center gap-2 mt-1">
                                 <div className="flex -space-x-2 overflow-hidden">
                                     {venue.shouts.slice(0, 3).map(s => (
                                         <img key={s.id} src={s.user.photoURL} className="inline-block h-5 w-5 rounded-full ring-2 ring-white object-cover bg-brand-cream-dark" />
                                     ))}
                                 </div>
                                 <p className="text-xs text-brand-dark-light font-medium truncate">
                                     {venue.shouts[0].user.username === 'me' ? 'You' : venue.shouts[0].user.displayName.split(' ')[0]} 
                                     {venue.shouts.length > 1 ? ` +${venue.shouts.length - 1}` : ''}
                                 </p>
                             </div>
                         </div>
                     </li>
                 );
             })
         )}
      </ul>
    </div>
  );
};